import React, {
    useState,
    useRef
} from 'react';

import {
    createPortal
} from 'react-dom';


import {
    useCurationConfigState
} from '../../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../../components/MetricLoadingView.jsx';

import {
    RIQInfoPopover,
    RIQPopover
} from '../../../../components/RIQPopover.jsx';

import useListeningCurationFilterValues from '../../hooks/useListeningCurationFilterValues.js';

import {
    useVerifySearch
} from '../../hooks/useVerifySearch.js';

import {
    useBlueColorPalette
} from '../../hooks/useColorPalette.jsx';

import RIQWordCloud from '../../../../components/RIQWordCloud.jsx';
import useContentModal from '../../hooks/useContentModal.jsx';
import ContextMenu from './ContextMenu.jsx';

import {
    listeningDefinitions
} from '../enums/listeningDefinitions.jsx';

const {
    associatedTermsDescription
} = listeningDefinitions['saved-search'];

const HEIGHT = 420;
const MAX_WORDS = 50;
const TOOLTIP_DELAY_MS = 250;
// NOTE this is a padded figure, and if the contents of the tooltip change this
// value will need to be updated, or an alternative method for determining the
// effective tooltip width from the element must be discovered
const TOOLTIP_WIDTH = 160;

export default function AssociatedTerms({instanceId}) {

    const [searchTerms] = useCurationConfigState('searchTerms');
    const [searchTermsAnd, setSearchTermsAnd] = useCurationConfigState('searchTermsAnd');
    const [searchTermsNot, setSearchTermsNot] = useCurationConfigState('searchTermsNot');

    const colorPalette = useBlueColorPalette();

    const {showContentModal, ContentModal} = useContentModal('associated-terms');

    const {orderedErrors} = useVerifySearch(false, {
        searchTerms: searchTerms,
        searchTermsAnd: searchTermsAnd,
        searchTermsNot: searchTermsNot
    });

    const hasValidationError = _.find(orderedErrors, {
        isFatal: true
    });
    const metricFilters = useListeningCurationFilterValues(instanceId, 'phrase');
    metricFilters.listening.limit = MAX_WORDS;

    const metricRequests = {};

    if (!hasValidationError) {
        metricRequests.dimensionValue = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['phrase'],
            measure: {
                field: 'count',
                returns: `dimensionValue(phrase)`
            },
            filters: metricFilters
        };

        metricRequests.counts = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['phrase'],
            measure: 'count',
            filters: metricFilters
        };
    }


    const [isTooltipFocused, setIsTooltipFocused] = useState(false);
    const [tooltipData, setTooltipData] = useState(null);
    const [tooltipLeft, setTooltipLeft] = useState(0);
    const [tooltipTop, setTooltipTop] = useState(0);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipPlacement, setTooltipPlacement] = useState('right');
    const tooltipOpenDelayRef = useRef(null);

    const tooltipVisible = tooltipOpen || isTooltipFocused;
    const onWordMouseOver = (word, wordBounds) => {
        let placement = 'right';
        let leftOffset = wordBounds.globalX + wordBounds.width;
        const width = $(window).width();
        if (width - leftOffset < TOOLTIP_WIDTH) {
            if (wordBounds.globalX < TOOLTIP_WIDTH) {
                leftOffset = leftOffset / 2;
                placement = 'top';
            } else {
                leftOffset = wordBounds.globalX;
                placement = 'left';
            }
        }

        setTooltipData(word);
        setTooltipLeft(leftOffset);
        setTooltipTop(wordBounds.globalY + wordBounds.height / 2);
        setTooltipPlacement(placement);

        if (tooltipOpenDelayRef.current) {
            clearTimeout(tooltipOpenDelayRef.current);
            tooltipOpenDelayRef.current;
        }

        // This delay is so that these context menus don't show up when the user is just moving the mouse around the word cloud
        tooltipOpenDelayRef.current = setTimeout(() => {
            setTooltipOpen(true);
        }, TOOLTIP_DELAY_MS);
    };

    const onWordMouseOut = () => {
        setTooltipOpen(false);
        if (tooltipOpenDelayRef.current) {
            clearTimeout(tooltipOpenDelayRef.current);
            tooltipOpenDelayRef.current = null;
        }
    };

    function onClickInclude(value) {
        if (_.isEmpty(searchTermsAnd?.[0])) {
            setSearchTermsAnd([[value]]);
        } else {
            const newTerms = _.cloneDeep(searchTermsAnd?.[0] || []);
            newTerms.push(value);
            setSearchTermsAnd([newTerms]);
        }

        setTooltipOpen(false);
        setIsTooltipFocused(false);
    }

    function onClickExclude(value) {
        if (_.isEmpty(searchTermsNot)) {
            setSearchTermsNot([value]);
        } else {
            const newTerms = _.cloneDeep(searchTermsNot);
            newTerms.push(value);
            setSearchTermsNot(newTerms);
        }

        setTooltipOpen(false);
        setIsTooltipFocused(false);
    }

    function onSampleClick(value) {
        setTooltipOpen(false);
        setIsTooltipFocused(false);

        showContentModal({
            searchTermsAnd: [[value]]
        });
    }

    return (
        <div
            className='tlw-p-7.5'
        >
            <div
                className='tlw-relative' // this is needed for the tooltip to be positioned correctly.
                style={{
                    height: HEIGHT // this cannot be done with a tailwind class and still use the constant.
                }}
            >
                <MetricLoadingView
                    instanceId={instanceId}
                    metricRequests={metricRequests}>
                    {
                        (metricResults) => {

                            const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);
                            const dimensionValueFormatter = RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.dimensionValue, metricResults.dimensionValue, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated);

                            const allUsedTerms = _.union(searchTermsNot, searchTermsAnd?.[0], searchTerms);

                            // filter out emojis and terms that have already been used.
                            const words = _(collatedResults)
                                .reject(({counts}) => {
                                    const phrase = counts[0];
                                    return phrase.match(/\p{Emoji}/u) || allUsedTerms.some(term => term.toLowerCase() === phrase.toLowerCase());
                                })
                                .map(({counts}, idx) => {
                                    return {
                                        text: dimensionValueFormatter(counts[0]),
                                        _actualText: counts[0],
                                        dimension: 'phrase',
                                        value: counts[1],
                                        className: `${colorPalette(idx)} hover_tlw-opacity-60`
                                    };
                                })
                                .value();

                            return (
                                <>
                                    <div className='tlw-flex'
                                    >
                                        <div className='tlw-mr-[0.275rem] tlw-text-11 tlw-font-semibold tlw-uppercase tlw-leading-8 tlw-text-gray-700'>Associated Terms</div>
                                        <RIQInfoPopover
                                            description={associatedTermsDescription}
                                            portalNode={document.body}
                                            isDescriptionHtml={true}
                                        />
                                    </div>

                                    <RIQWordCloud
                                        words={words}
                                        onWordMouseOver={onWordMouseOver}
                                        onWordMouseOut={onWordMouseOut}
                                    />
                                </>
                            );
                        }
                    }
                </MetricLoadingView>

                {tooltipVisible && (
                    // This portal allows the tooltip to be rendered outside of the word cloud container.
                    createPortal(
                        <div
                            className='riq-tooltip-container tlw-pointer-events-auto tlw-rounded-sm tlw-border tlw-border-solid tlw-border-black tlw-bg-black tlw-text-md tlw-text-white'
                            onMouseEnter={() => setIsTooltipFocused(true)}
                            onMouseLeave={() => setIsTooltipFocused(false)}
                            style={{
                                left: tooltipLeft,
                                top: tooltipTop,
                                textShadow: 'none'
                            }}
                        >
                            <RIQPopover
                                triggerNode={<div/>}
                                className='tlw-flex tlw-w-full tlw-items-center tlw-gap-5 tlw-text-md tlw-font-normal'
                                placement={tooltipPlacement}
                                additionalHookProps={{
                                    visible: true
                                }}
                            >
                                <ContextMenu
                                    value={tooltipData?._actualText}
                                    valueName={tooltipData?.dimension}
                                    onClickInclude={onClickInclude}
                                    onClickExclude={onClickExclude}
                                    onSampleClick={onSampleClick}
                                />

                            </RIQPopover>
                        </div>,
                        document.body
                    )
                )}

                {ContentModal}
            </div>
        </div>
    );
}
